import {
  Button,
  Col,
  Collapse,
  CollapsePanelProps,
  Descriptions,
  Divider,
  Empty,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useMatch } from 'react-router-dom';

import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import ErrorResult from '../components/ErrorResult';
import useCanAccessJobPosting from '../hooks/useCanAccessJobPosting';
import useCreateInterviewMutation from '../hooks/useCreateInterviewMutation';
import usePrefetchPages from '../hooks/usePrefetchPages';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams, { Optional } from '../hooks/useTypedParams';
import { CandidateId, JobPostingId, RecruitmentProcessCollection, StageCollection } from '../types';
import { InterviewCollectionSerializer } from '../types/api';
import styles from './ShowCandidateInterviews.module.css';

interface StageInterviewPanelProps extends CollapsePanelProps {
  stage: StageCollection;
  canManage?: boolean;
  interview?: InterviewCollectionSerializer;
  recruitmentProcessId: RecruitmentProcessCollection['id'];
  onInterviewScriptShowMore: (
    name: string,
    content: string,
    candidateContent: string,
    isPracticalTechnicalCase: boolean
  ) => void;
  onAnnotationsShowMore: (annotations?: string) => void;
  isCurrentStage: boolean;
}

const tagStyle = {
  width: '90%',
  padding: '5px 0 5px 0',
  textAlign: 'center',
} as React.CSSProperties;

const StageInterviewPanel = ({
  stage,
  interview,
  recruitmentProcessId,
  onAnnotationsShowMore,
  onInterviewScriptShowMore,
  canManage,
  isCurrentStage = false,
  ...props
}: StageInterviewPanelProps) => {
  const { id, tenant } = useTypedParams({
    id: JobPostingId,
    applicant_id: CandidateId,
    tenant: String,
  });

  const queryClient = useQueryClient();
  const navigate = useTypedNavigate();

  const createInterview = useCreateInterviewMutation({
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries('/interviews/');
      navigate({
        pathname: '/:tenant/opportunities/:id/interview/:interview_id/',
        params: {
          tenant,
          id,
          interview_id: data.id,
        },
      });
    },
  });

  const interviewScriptTemplate = useAPIQuery(
    [`/interview-script-templates/${stage.interview_script_template}/`],
    {
      enabled: Boolean(stage.interview_script_template && !stage.lock_interview_script),
    }
  );

  const interviewScriptTemplateEmailCommunicationTemplate = useAPIQuery(
    `/email-communication-templates/${interviewScriptTemplate.data?.email_communication_template}/`,
    {
      enabled:
        stage.sub_type === 'PRACTICAL_TECHNICAL_CASE' &&
        !stage.email_communication_template &&
        !!interviewScriptTemplate.data?.email_communication_template,
    }
  );

  const stageEmailCommunicationTemplatePracticalTechnicalCase = useAPIQuery(
    `/email-communication-templates/${stage.email_communication_template}/`,
    {
      enabled:
        stage.sub_type === 'PRACTICAL_TECHNICAL_CASE' && !!stage.email_communication_template,
    }
  );

  return (
    <Collapse.Panel {...props}>
      <Space direction="vertical">
        {isCurrentStage && canManage && interview?.status !== 'FINISHED' && (
          <Button
            onClick={() => {
              if (!interview) {
                return createInterview.mutate({
                  recruitmentProcessId,
                  stageId: stage.id,
                });
              }

              navigate({
                pathname: '/:tenant/opportunities/:id/interview/:interview_id/',
                params: {
                  tenant,
                  id,
                  interview_id: interview.id,
                },
              });
            }}
          >
            Iniciar entrevista
          </Button>
        )}
        <Descriptions
          layout="vertical"
          size="small"
          colon={false}
          labelStyle={{ fontWeight: 'bold' }}
        >
          <Descriptions.Item label="Roteiro da etapa" span={1}>
            {stage.lock_interview_script ? (
              <Button
                disabled={
                  !stage.interview_script_content &&
                  !stage.interview_script_template &&
                  !stageEmailCommunicationTemplatePracticalTechnicalCase.data?.message
                }
                className={styles.scriptbutton}
                onClick={() =>
                  onInterviewScriptShowMore(
                    stage.interview_script_name,
                    stage.interview_script_content as string,
                    stageEmailCommunicationTemplatePracticalTechnicalCase.data?.message || '',
                    stage.sub_type === 'PRACTICAL_TECHNICAL_CASE'
                  )
                }
              >
                {stage.interview_script_name || '-'}
              </Button>
            ) : (
              <Button
                disabled={
                  !stage.interview_script_content &&
                  !stage.interview_script_template &&
                  !stageEmailCommunicationTemplatePracticalTechnicalCase.data?.message
                }
                className={styles.scriptbutton}
                onClick={() => {
                  if (interviewScriptTemplate.data) {
                    return onInterviewScriptShowMore(
                      interviewScriptTemplate.data.name,
                      interviewScriptTemplate.data.content,
                      interviewScriptTemplateEmailCommunicationTemplate.data?.message || '',
                      stage.sub_type === 'PRACTICAL_TECHNICAL_CASE'
                    );
                  }
                  return onInterviewScriptShowMore(
                    'Personalizado',
                    stage.interview_script_content || '',
                    stageEmailCommunicationTemplatePracticalTechnicalCase.data?.message || '',
                    stage.sub_type === 'PRACTICAL_TECHNICAL_CASE'
                  );
                }}
              >
                {!stage.interview_script_template &&
                (stage.interview_script_content ||
                  stageEmailCommunicationTemplatePracticalTechnicalCase.data?.message)
                  ? 'Personalizado'
                  : interviewScriptTemplate.data?.name || '-'}
              </Button>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Parecer da etapa" span={1}>
            {(!interview || interview?.opinion_status === 'PENDING') && (
              <Tag style={tagStyle}>Pendente</Tag>
            )}
            {interview?.opinion_status === 'IN_ANALYSIS' && (
              <Tag style={tagStyle} color="yellow">
                Em análise
              </Tag>
            )}
            {interview?.opinion_status === 'QUALIFIED' && (
              <Tag style={tagStyle} color="blue">
                Qualificado
              </Tag>
            )}
            {interview?.opinion_status === 'NOT_QUALIFIED' && (
              <Tag style={tagStyle} color="red">
                Não qualificado
              </Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Anotações" span={1}>
            <Button
              style={{ width: '90%' }}
              onClick={() => onAnnotationsShowMore(interview?.annotations)}
              disabled={!interview?.annotations}
            >
              Exibir anotações
            </Button>
          </Descriptions.Item>
          <Descriptions.Item label="Justificativa">
            {interview?.opinion_justification ? (
              <span style={{ whiteSpace: 'pre-wrap' }}>{interview?.opinion_justification}</span>
            ) : (
              <span style={{ color: '#595959' }}>Ainda não há justificativas para essa etapa.</span>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Space>
    </Collapse.Panel>
  );
};

const ShowCandidateInterviews = () => {
  const { applicant_id, id } = useTypedParams({
    id: Optional(JobPostingId),
    applicant_id: CandidateId,
    tenant: String,
  });

  const [jobPostingActiveKey, setJobPostingActiveKey] = useState<string[]>([]);
  const [stageActiveKey, setStageActiveKey] = useState<string[]>([]);

  const recruitmentProcessesPages = useInfiniteAPIQuery([
    '/recruitment-processes/',
    {
      params: {
        candidate: applicant_id,
        job_posting: id,
      },
    },
  ]);

  const recruitmentProcesses = usePrefetchPages(recruitmentProcessesPages, (data) => data.results);

  const interviewPages = useInfiniteAPIQuery([
    '/interviews/',
    {
      params: {
        recruitment_process__in: recruitmentProcesses.map(({ id }) => id).join(','),
      },
    },
  ]);

  const interviews = usePrefetchPages(interviewPages, (data) => data.results);

  const stagePages = useInfiniteAPIQuery(
    [
      '/stages/',
      {
        params: {
          job_posting_id__in: recruitmentProcesses
            .map(({ job_posting }) => job_posting.id)
            .join(','),
          sub_type__in:
            'SCREENING_INTERVIEW,HR_INTERVIEW,MANAGER_INTERVIEW,STAKEHOLDERS_INTERVIEW,PRACTICAL_TECHNICAL_CASE',
          expand: 'interview_script_content',
        },
      },
    ],
    { enabled: recruitmentProcessesPages.isSuccess }
  );

  const stages = usePrefetchPages(stagePages, (data) => data.results);

  const { canManage } = useCanAccessJobPosting({ jobPostingId: id });

  const match = useMatch(
    ':tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/interviews/'
  );

  const [showMoreInterviewScript, setShowMoreInterviewScript] = useState<{
    name: string;
    content: string;
    candidateContent: string;
    isPracticalTechnicalCase: boolean;
  }>();
  const [showMoreAnnotations, setShowMoreAnnotations] = useState<string>();

  const jobPostingMap = recruitmentProcesses
    .map((recruitmentProcess) => ({
      recruitmentProcess,
      stages: stages.filter(({ job_posting }) => job_posting === recruitmentProcess.job_posting.id),
      jobPosting: recruitmentProcess.job_posting,
    }))
    .filter(({ stages }) => stages.length !== 0);

  return (
    <ErrorResult
      error={recruitmentProcessesPages.error || stagePages.error || interviewPages.error}
    >
      <Spin
        spinning={
          recruitmentProcessesPages.isLoading || stagePages.isLoading || interviewPages.isLoading
        }
      >
        <Modal
          title={<Typography.Text strong>Anotações da entrevista</Typography.Text>}
          open={Boolean(showMoreAnnotations)}
          closable
          maskClosable
          width={800}
          onCancel={() => setShowMoreAnnotations(undefined)}
          footer={[
            <Button
              key="show-more-annotations"
              type="primary"
              onClick={() => setShowMoreAnnotations(undefined)}
            >
              Fechar
            </Button>,
          ]}
          destroyOnClose
        >
          <div
            dangerouslySetInnerHTML={{ __html: showMoreAnnotations || '' }}
            style={{ maxHeight: 600, overflowY: 'scroll' }}
          />
        </Modal>
        <Modal
          title={<Typography.Text strong>{showMoreInterviewScript?.name}</Typography.Text>}
          open={Boolean(showMoreInterviewScript)}
          closable
          maskClosable
          width={800}
          onCancel={() => setShowMoreInterviewScript(undefined)}
          footer={[
            <Button
              key="show-more-interview-script"
              type="primary"
              onClick={() => setShowMoreInterviewScript(undefined)}
            >
              Fechar
            </Button>,
          ]}
          destroyOnClose
        >
          {showMoreInterviewScript?.isPracticalTechnicalCase ? (
            <Row gutter={[16, 16]}>
              <Col span={11}>
                <Typography.Title level={4}>Instruções para o avaliador</Typography.Title>
                <div
                  dangerouslySetInnerHTML={{
                    __html: showMoreInterviewScript?.content || '',
                  }}
                  style={{ maxHeight: 600, overflowY: 'scroll' }}
                />
              </Col>
              <Divider type="vertical" style={{ maxHeight: 600, height: 'inherit' }} />
              <Col span={11}>
                <Typography.Title level={4}>Descrição para o candidato</Typography.Title>
                <div
                  dangerouslySetInnerHTML={{
                    __html: showMoreInterviewScript?.candidateContent || '',
                  }}
                  style={{ maxHeight: 600, overflowY: 'scroll' }}
                />
              </Col>
            </Row>
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: showMoreInterviewScript?.content || '',
              }}
              style={{ maxHeight: 600, overflowY: 'scroll' }}
            />
          )}
        </Modal>
        {id ? (
          <>
            {(stages.length ?? 0) > 0 ? (
              <Collapse
                ghost
                expandIconPosition="end"
                activeKey={stageActiveKey}
                className={styles.collapse}
                onChange={(activeKey) => setStageActiveKey(activeKey as string[])}
              >
                {stages.map((stage) => (
                  <StageInterviewPanel
                    key={stage.id}
                    header={<Typography.Title level={4}>{stage.name || '...'}</Typography.Title>}
                    stage={stage}
                    interview={interviews.find((interview) => interview.stage === stage.id)}
                    recruitmentProcessId={
                      recruitmentProcesses[0]?.id as RecruitmentProcessCollection['id']
                    }
                    canManage={canManage && !match}
                    onInterviewScriptShowMore={(
                      name,
                      content,
                      candidateContent,
                      isPracticalTechnicalCase
                    ) =>
                      setShowMoreInterviewScript({
                        name,
                        content,
                        candidateContent,
                        isPracticalTechnicalCase,
                      })
                    }
                    isCurrentStage={recruitmentProcesses[0]?.stage.id === stage.id}
                    onAnnotationsShowMore={(annotations) => setShowMoreAnnotations(annotations)}
                  />
                ))}
              </Collapse>
            ) : (
              <Empty />
            )}
          </>
        ) : (
          <>
            {jobPostingMap.length > 0 ? (
              <Collapse
                ghost
                expandIconPosition="end"
                activeKey={jobPostingActiveKey}
                className={styles.collapse}
                onChange={(activeKey) => setJobPostingActiveKey(activeKey as string[])}
              >
                {jobPostingMap.map(({ jobPosting, stages, recruitmentProcess }) => (
                  <Collapse.Panel
                    key={jobPosting.id}
                    header={
                      <Typography.Title level={4}>{jobPosting.name || '...'}</Typography.Title>
                    }
                  >
                    <Collapse
                      ghost
                      expandIconPosition="end"
                      activeKey={stageActiveKey}
                      onChange={(activeKey) => setStageActiveKey(activeKey as string[])}
                    >
                      {stages.map((stage) => (
                        <StageInterviewPanel
                          key={stage.id}
                          header={<Typography.Text>{stage.name || '...'}</Typography.Text>}
                          stage={stage}
                          interview={interviews.find((interview) => interview.stage === stage.id)}
                          recruitmentProcessId={
                            recruitmentProcess.id as RecruitmentProcessCollection['id']
                          }
                          canManage={false}
                          onInterviewScriptShowMore={(
                            name,
                            content,
                            candidateContent,
                            isPracticalTechnicalCase
                          ) =>
                            setShowMoreInterviewScript({
                              name,
                              content,
                              candidateContent,
                              isPracticalTechnicalCase,
                            })
                          }
                          isCurrentStage={recruitmentProcess.stage.id === stage.id}
                          onAnnotationsShowMore={(annotations) =>
                            setShowMoreAnnotations(annotations)
                          }
                        />
                      ))}
                    </Collapse>
                  </Collapse.Panel>
                ))}
              </Collapse>
            ) : (
              <Empty />
            )}
          </>
        )}
      </Spin>
    </ErrorResult>
  );
};

export default ShowCandidateInterviews;
